/* eslint-disable max-nested-callbacks */
import { createSelector } from 'reselect';
import { getMapData } from './map';
import { getFiltersFlat } from './filters';
import * as R from 'ramda';

const getRawMapData = state => state.map.data;

export const getGeocoderSuggestions = state => state.search.suggestions.geocoder;
export const getPlacesSuggestions = state => state.search.suggestions.places;
export const getSearchTerm = state => state.search.searchTerm;
export const getSearchMarker = state => state.search.showMarker;
export const getSearchFocused = state => state.search.focused;
export const getSelectedLocation = state => state.search.selectedLocation;
export const getselectedLayerElement = state => state.search.selectedLayerElement;
export const getShowRecordSearchTray = state => state.search.showRecordSearchTray;
export const getRecordSearchQuery = state => state.search.recordSearchQuery;

export const getSelection = createSelector(
  [getSelectedLocation, getselectedLayerElement],
  (location, layer) => location || layer || null
);

export const getSelectedPosition = createSelector(
  [getSelectedLocation],
  (location) => location && location.position
);

export const getSearchableLayerData = createSelector(
  [getMapData], mapData => (Object.values(mapData).filter(layerData => layerData.uiStyle.searchable))
);

// All layers with unfiltered list data — bypasses date range so all records are searchable
const getRecordLayerData = createSelector(
  [getRawMapData, getFiltersFlat],
  (rawData, filtersFlat) => {
    if (!rawData || !filtersFlat) {
      return [];
    }
    return Object.values(rawData)
      .filter(layerData => filtersFlat[layerData.id])
      .map(layerData => {
        const {requestUrl, mapStyle, uiStyle, ...filter} = filtersFlat[layerData.id];
        return {...layerData, ...filter, url: requestUrl, mapStyle: {...mapStyle, clickable: false}, uiStyle};
      });
  }
);

// Memoize truthy result as long as search remains the same:
let lastSearch = null;
let lastResult = null;
export const getLinkedElement = createSelector(
  [getSearchableLayerData, () => window.location.search],
  (searchableLayers, search) => {
    if (lastSearch === search && lastResult) {
      return lastResult;
    }
    lastSearch = search;
    const links = [];
    search.substr(1).split(',')
      .forEach(param => {
        const [paramName, searchValue] = param.split('=');
        searchableLayers.filter(layerData => paramName === layerData.name).forEach(layerData => {
          const {id: layerId, list, uiStyle: {searchKey, searchLabel, searchGroup}} = layerData;
          links.push(...(
            list.filter(element => element.attrs[searchKey] === searchValue)
              .map(({id, bbox, attrs: {[searchKey]: match}}) => (
                { id, bbox, label: `${searchLabel} ${match}`, layerId, type: searchGroup }
              ))
          ));
        });
      });
    lastResult = links[0] || null;
    return lastResult;
  }
);

const getGeocodeSuggestions = createSelector(
  [getGeocoderSuggestions, getPlacesSuggestions],
  (geocoderSuggestions, placesSuggestions) => {
    if (geocoderSuggestions && placesSuggestions) {
      if (geocoderSuggestions.length || placesSuggestions.length) {
        const filteredGeocoding = geocoderSuggestions.filter(
          ({types}) => types.indexOf('intersection') > -1 || types.indexOf('postal_code') > -1
        );
        const suggestions = filteredGeocoding.concat(placesSuggestions.filter(suggestion =>
          !R.find(R.propEq(suggestion.place_id, 'place_id'), filteredGeocoding)
        ));
        if (suggestions.length > 0) {
          return [...suggestions.slice(0, 5)];
        }
      }
      return [];
    }
    return null;
  }
);

const buildRecordMatches = (searchValue, layerDatas) => {
  if (!searchValue) {
    return null;
  }
  const searchLower = searchValue.toLowerCase();
  const allMatches = [];

  layerDatas.forEach(layerData => {
    const { id: layerId, name: layerName, label: layerLabel, uiStyle } = layerData;
    const { searchGroup, searchKey, searchLabel } = uiStyle || {};
    const effectiveSearchKey = searchKey || 'name';
    const effectiveSearchLabel = searchLabel || layerLabel || '';
    const effectiveSearchGroup = searchGroup || layerName || '';

    (layerData.list || []).forEach(element => {
      const attrs = element.attrs || {};
      const nameVal = attrs[effectiveSearchKey] || '';
      const nameMatch = Boolean(
        nameVal &&
        `${effectiveSearchLabel} ${nameVal}`
          .toLowerCase()
          .includes(searchLower)
      );
      const idMatch = Boolean(
        element.remote_id &&
        String(element.remote_id)
          .toLowerCase()
          .includes(searchLower)
      );
      if (nameMatch || idMatch) {
        allMatches.push({
          id: element.id,
          bbox: element.bbox,
          remote_id: element.remote_id,
          entityId: attrs.id !== null ? attrs.id : null,
          label: nameVal,
          typeLabel: effectiveSearchLabel,
          name: nameVal,
          start: element.start,
          end: element.end,
          value: `layer-${element.id}`,
          layerId,
          type: effectiveSearchGroup
        });
      }
    });
  });

  // Group by entityId when present, then remote_id, then pk-${id}
  const groupKey = match => {
    if (match.entityId !== null) {
      return `entity-${match.entityId}`;
    }
    if (match.remote_id) {
      return `remote-${match.remote_id}`;
    }
    return `pk-${match.id}`;
  };

  const groups = {};
  allMatches.forEach(match => {
    const key = groupKey(match);
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(match);
  });

  const seen = new Set();
  const result = allMatches
    .filter(match => {
      const key = groupKey(match);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    })
    .map(match => {
      const members = groups[groupKey(match)];
      return {
        ...match,
        locationCount: members.length,
        groupMembers: members.map(member => ({id: member.id, layerId: member.layerId, bbox: member.bbox}))
      };
    });
  return result;
};

// Returns up to 5 record matches for the autocomplete dropdown
export const getRecordSuggestions = createSelector(
  [getSearchTerm, getRecordLayerData],
  (searchValue, layerDatas) => {
    const matches = buildRecordMatches(searchValue, layerDatas);
    if (matches === null) {
      return null;
    }
    return matches.slice(0, 5);
  }
);

// Returns all record matches (no cap) for the "See all" tray, using the stored query
export const getAllRecordMatches = createSelector(
  [getRecordSearchQuery, getRecordLayerData],
  (searchValue, layerDatas) => buildRecordMatches(searchValue, layerDatas) || []
);

// Returns all record matches for the current live search term (used by "See all" in the dropdown)
export const getAllRecordMatchesBySearchTerm = createSelector(
  [getSearchTerm, getRecordLayerData],
  (searchValue, layerDatas) => buildRecordMatches(searchValue, layerDatas) || []
);

export const getSuggestions = createSelector(
  [getRecordSuggestions, getGeocodeSuggestions],
  (recordSuggestions, locationSuggestions) => {
    if (recordSuggestions === null) {
      return null;
    }

    const result = [];

    if (recordSuggestions.length > 0) {
      result.push(
        { label: 'Records', value: 'header-records', isDisabled: true, header: true },
        { label: 'See all record results', value: 'see-all', isSeeAll: true },
        ...recordSuggestions
      );
    }

    if (locationSuggestions && locationSuggestions.length > 0) {
      if (result.length > 0) {
        result.push({ value: 'divider', isDivider: true, isDisabled: true });
      }
      result.push(
        { label: 'Locations', value: 'header-locations', isDisabled: true, header: true },
        ...locationSuggestions
      );
    }

    return result;
  }
);

export const getIsLoading = createSelector(
  [getSearchTerm, getGeocoderSuggestions, getPlacesSuggestions],
  (searchTerm, geocoder, places) => Boolean(searchTerm && (geocoder === null || places === null))
);

export const getMenuIsOpen = createSelector(
  [getSearchTerm, getSuggestions],
  (searchTerm, suggestions) => Boolean(searchTerm && suggestions && suggestions.length > 0)
);
